import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import PropTypes from 'prop-types';

import styles from './styles/FormSelect.module.css';

const FormSelect = ({
    value,
    onChange,
    options = [],
    placeholder = 'Select an option',
    disabled = false,
    required = false,
    className = '',
    id,
    name,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState('bottom');
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Position dropdown
    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // If not enough space below and more space above, position on top
            if (spaceBelow < 200 && spaceAbove > spaceBelow) {
                setDropdownPosition('top');
            } else {
                setDropdownPosition('bottom');
            }
        }
    }, [isOpen]);

    const handleToggle = useCallback(() => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    }, [disabled, isOpen]);

    const handleOptionSelect = useCallback(optionValue => {
        if (onChange) {
            onChange(optionValue);
        }
        setIsOpen(false);
    }, [onChange]);

    const getSelectedOption = useCallback(() => {
        return options.find(option => option.value === value);
    }, [options, value]);

    const selectedOption = getSelectedOption();

    return (
        <div className={`${styles.container} ${className}`} ref={containerRef} {...props}>
            <button
                type="button"
                className={`${styles.selector} ${isOpen ? styles.open : ''} ${disabled ? styles.disabled : ''}`}
                onClick={handleToggle}
                disabled={disabled}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label={`Select ${name || 'option'}`}
                id={id}
            >
                <span className={styles.selectedText}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown size={16} className={styles.chevron} />
            </button>

            {isOpen && (
                <div
                    ref={dropdownRef}
                    className={`${styles.dropdown} ${styles[dropdownPosition]}`}
                    role="listbox"
                >
                    {options.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={`${styles.option} ${option.disabled ? styles.optionDisabled : ''}`}
                            onClick={() => !option.disabled && handleOptionSelect(option.value)}
                            disabled={option.disabled}
                            role="option"
                            aria-selected={value === option.value}
                        >
                            <div className={styles.optionContent}>
                                <span className={styles.optionLabel}>{option.label}</span>
                                {option.description && (
                                    <span className={styles.optionDescription}>
                                        {option.description}
                                    </span>
                                )}
                            </div>
                            {value === option.value && (
                                <div className={styles.checkIcon}>
                                    <Check size={11} />
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

FormSelect.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
        description: PropTypes.string,
        disabled: PropTypes.bool,
    })),
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    className: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
};

export default memo(FormSelect);
